import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Divider,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Slider,
  Grid,
} from '@mui/material';
import {
  Person as PersonIcon,
  AccessTime as TimeIcon,
  MenuBook as BookIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Autorenew as RefreshIcon,
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import { Tabs } from '../components/ui/Tabs';
import { PageHeader } from '../components/ui/PageHeader';
import TrainingInsightsTab, { TrainingInsights } from '../components/training/TrainingInsightsTab';
import TrackTypeBadge from '../components/training/TrackTypeBadge';
import type { TrainingQuestionDetection } from '../components/training/TrainingDetectionCard';
import TrainingTimelineEvent from '../components/training/TrainingTimelineEvent';
import SaveInquiryModal from '../components/SaveInquiryModal';
import { useTrainingInquiryActions } from '../hooks/useTrainingInquiryActions';
import { apiService } from '../services/apiService';
import { useRoleAccess } from '../hooks/useRoleAccess';
import moment from 'moment';
import { DATE_TIME_FORMAT, TIME_FORMAT } from '../constants/dateFormats';

interface TrainingSession {
  id: string; // Mongoose _id transformed to id by toJSON
  _id?: string; // Fallback for backward compatibility
  sessionId: string;
  trackId: string;
  trackName: string;
  trackType: 'kb' | 'playbook';
  userId: string;
  userName: string;
  userEmail: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  duration?: number;
  transcripts: Array<{
    speaker: 'salesperson' | 'customer' | 'ai_customer';
    text: string;
    timestamp: string;
    trackItemId?: string;
  }>;
  itemResults: Array<{
    trackItemId: string;
    itemType: string;
    question: string;
    expectedAnswer?: string; // Only for objection/faq
    canSay?: any[]; // Only for landmine
    cannotSay?: any[]; // Only for landmine
    advantages?: any[]; // Only for competitor
    weaknesses?: any[]; // Only for competitor
    /** The rep's words for this item ('' when they did not answer). */
    userResponse?: string;
    /** Knowledge base read-out sessions only; scoring happens after the session. */
    status?: 'answered' | 'no_answer';
    // Live-evaluation fields: written by Playbook Live and by sessions
    // recorded before the read-out mode. Absent on new knowledge base sessions.
    responseQuality?: number;
    keywordsCovered?: string[];
    keywordsMissed?: string[];
    landminesTriggered?: number;
    aiEvaluation?: string;
    timestamp: string;
  }>;
  landmineDetections?: Array<{
    trigger: string;
    matched_text: string;
    category: string;
    priority: string;
    timestamp: string;
    speaker: string;
  }>;
  competitorDetections?: Array<{
    competitor: string;
    matched_text: string;
    timestamp: string;
    speaker: string;
  }>;
  // Playbook Live only: the question cards the rep saw during the call.
  detectedObjections?: TrainingQuestionDetection[];
  detectedFAQs?: TrainingQuestionDetection[];
  newInquiries?: TrainingQuestionDetection[];
  trainingInsights?: TrainingInsights;
  adminEvaluation?: {
    evaluatedBy: string;
    evaluatedByEmail: string;
    finalScore: number;
    comments: string;
    strengths: string[];
    improvements: string[];
    evaluatedAt: string;
  };
}

const TrainingSessionEvaluation: React.FC = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [session, setSession] = useState<TrainingSession | null>(null);
  const [isEditingEvaluation, setIsEditingEvaluation] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // Timeline-jump: an insight item's "View in timeline" link sets this;
  // an effect then switches to the Session Details tab and scrolls.
  const [scrollToTimestamp, setScrollToTimestamp] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Evaluation form
  const [finalScore, setFinalScore] = useState(0);
  const [comments, setComments] = useState('');
  const [strengths, setStrengths] = useState<string[]>([]);
  const [improvements, setImprovements] = useState<string[]>([]);
  const [strengthInput, setStrengthInput] = useState('');
  const [improvementInput, setImprovementInput] = useState('');

  const inquiries = useTrainingInquiryActions({
    sessionId: session?.id || session?._id,
    newInquiries: session?.newInquiries,
    onSuccess: setSuccess,
    onError: setError,
  });

  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    }
    // loadSession is a plain function declared below this effect, so listing it would reference
    // the binding before initialisation. It is called with the sessionId already in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadSession = async (id: string, silent: boolean = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      // Try admin endpoint first if admin, otherwise user endpoint
      const endpoint = isAdmin
        ? `/training/admin/sessions/${id}`
        : `/training/sessions/${id}`;

      const response = await apiService.get(endpoint);

      if (response.success && response.data) {
        const sessionData = response.data;
        setSession(sessionData);

        // Load existing evaluation if present
        if (sessionData.adminEvaluation) {
          setFinalScore(sessionData.adminEvaluation.finalScore || 0);
          setComments(sessionData.adminEvaluation.comments || '');
          setStrengths(sessionData.adminEvaluation.strengths || []);
          setImprovements(sessionData.adminEvaluation.improvements || []);
        }
      }
    } catch (error: any) {
      console.error('Error loading session:', error);
      if (!silent) {
        setError(error.message || 'Failed to load session');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Poll trainingInsights while analysis is still running on the backend.
  // post_training_analysis_service writes the document incrementally
  // (dimensionsComplete grows as each parallel dimension finishes), so a
  // simple 5s refetch is enough to keep the Insights tab live without any
  // WebSocket plumbing.
  //
  // Polling conditions:
  //   - session.status is 'completed' or 'abandoned' (analysis is eligible)
  //   - trainingInsights is undefined OR status === 'pending'
  // Stops on:
  //   - status === 'completed', 'failed' or 'not_scoreable' → analysis is done
  //   - 30 attempts elapsed (~2.5 min)      → safety bound
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!sessionId || !session) return;

    const sessionStatus = (session as any).status;
    const insightsStatus = (session as any).trainingInsights?.status;
    const hasInsights = (session as any).trainingInsights !== undefined;

    // Only poll for sessions that have finished (so analysis has been triggered)
    // and whose insights are not yet final.
    const eligibleStatus = sessionStatus === 'completed' || sessionStatus === 'abandoned';
    const stillPending = !hasInsights || insightsStatus === 'pending';

    if (!eligibleStatus || !stillPending) return;

    let attempts = 0;
    const MAX_ATTEMPTS = 30; // 30 × 5s = 2.5 min safety bound

    const intervalId = setInterval(() => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        clearInterval(intervalId);
        return;
      }
      loadSession(sessionId, true /* silent */);
    }, 5000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, (session as any)?.status, (session as any)?.trainingInsights?.status]);

  const handleAddStrength = () => {
    if (strengthInput.trim() && !strengths.includes(strengthInput.trim())) {
      setStrengths([...strengths, strengthInput.trim()]);
      setStrengthInput('');
    }
  };

  const handleAddImprovement = () => {
    if (improvementInput.trim() && !improvements.includes(improvementInput.trim())) {
      setImprovements([...improvements, improvementInput.trim()]);
      setImprovementInput('');
    }
  };

  const handleSaveEvaluation = async () => {
    if (!session) return;

    try {
      setSaving(true);
      setError(null);

      const evaluationData = {
        finalScore,
        comments,
        strengths,
        improvements,
      };

      const sessionId = session.id || session._id;
      if (!sessionId) {
        throw new Error('Session ID not found');
      }

      const response = await apiService.post(
        `/training/admin/sessions/${sessionId}/evaluate`,
        evaluationData
      );

      if (response.success) {
        setSuccess('Evaluation saved successfully!');
        setIsEditingEvaluation(false);
        // Reload session to get updated data
        await loadSession(sessionId);
      }
    } catch (error: any) {
      console.error('Error saving evaluation:', error);
      setError(error.message || 'Failed to save evaluation');
    } finally {
      setSaving(false);
    }
  };

  // Re-run post-training analysis. The backend clears trainingInsights,
  // which makes the analyzer pick the session up again. The existing
  // polling effect then refreshes the Insights tab as the new run lands.
  const handleReanalyze = async () => {
    const sid = session?.id || session?._id;
    if (!sid) return;
    try {
      setReanalyzing(true);
      setError(null);
      const response = await apiService.post(
        `/training/admin/sessions/${sid}/reanalyze`
      );
      if (response.success) {
        setSuccess('Re-analysis queued. Results will refresh automatically.');
        await loadSession(sid, true /* silent */);
      }
    } catch (err: any) {
      console.error('Error re-running analysis:', err);
      setError(err.message || 'Failed to re-run analysis');
    } finally {
      setReanalyzing(false);
    }
  };

  // Insight item → conversation timeline navigation. Switches to the
  // Session Details tab (which holds the timeline) and records the target
  // timestamp; the effect below scrolls to the closest timeline event.
  const handleNavigateToTimestamp = useCallback((ts: string) => {
    setActiveTab(0);
    setScrollToTimestamp(ts);
  }, []);

  useEffect(() => {
    if (activeTab !== 0 || !scrollToTimestamp) return;

    const targetMs = new Date(scrollToTimestamp).getTime();
    if (isNaN(targetMs)) {
      setScrollToTimestamp(null);
      return;
    }

    // Let the timeline render before scanning for [data-ts] elements.
    const timerId = setTimeout(() => {
      let closest: HTMLElement | null = null;
      let minDiff = Infinity;
      document.querySelectorAll<HTMLElement>('[data-ts]').forEach((el) => {
        const elMs = new Date(el.dataset.ts || '').getTime();
        if (isNaN(elMs)) return;
        const diff = Math.abs(elMs - targetMs);
        if (diff < minDiff) {
          minDiff = diff;
          closest = el;
        }
      });

      const target = closest as HTMLElement | null;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('ring-2', 'ring-signature', 'ring-offset-2', 'transition-all');
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
          target.classList.remove('ring-2', 'ring-signature', 'ring-offset-2', 'transition-all');
        }, 3000);
      }
      setScrollToTimestamp(null);
    }, 80);

    return () => clearTimeout(timerId);
  }, [activeTab, scrollToTimestamp]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const roundedSeconds = Math.floor(seconds);
    const minutes = Math.floor(roundedSeconds / 60);
    const secs = roundedSeconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format(DATE_TIME_FORMAT);
  };

  if (loading) {
    return (
      <MainLayout title="Training Session Details">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!session) {
    return (
      <MainLayout title="Training Session Details">
        <Alert severity="error">Session not found</Alert>
      </MainLayout>
    );
  }

  // Training quality score (DB field behavioralScore) from post-training
  // analysis — null until analysis runs.
  const behavioralScore: number | null =
    session.trainingInsights?.overallCoaching?.behavioralScore ?? null;

  // Combine all timeline events
  const timelineEvents: any[] = [];

  // Add transcripts
  session.transcripts.forEach(transcript => {
    timelineEvents.push({
      type: 'transcript',
      timestamp: transcript.timestamp,
      data: transcript
    });
  });

  // Add item results
  (session.itemResults || []).forEach(result => {
    timelineEvents.push({
      type: 'evaluation',
      timestamp: result.timestamp,
      data: result
    });
  });

  // Add landmine detections
  if (session.landmineDetections) {
    session.landmineDetections.forEach(detection => {
      timelineEvents.push({
        type: 'landmine',
        timestamp: detection.timestamp,
        data: detection
      });
    });
  }

  // Add competitor detections
  if (session.competitorDetections) {
    session.competitorDetections.forEach(detection => {
      timelineEvents.push({
        type: 'competitor',
        timestamp: detection.timestamp,
        data: detection
      });
    });
  }

  // Add detected objections / FAQs / New Inquiries (Playbook Live)
  [
    ...(session.detectedObjections ?? []),
    ...(session.detectedFAQs ?? []),
    ...(session.newInquiries ?? []),
  ].forEach(detection => {
    timelineEvents.push({
      type: 'detection',
      timestamp: detection?.timestamp,
      data: detection
    });
  });

  // Sort by timestamp
  timelineEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  return (
    <MainLayout title="Training Session Details">
      <Box className="p-2">
        <PageHeader title="Training Session Details" backLabel="Back to Training" onBack={() => navigate('/training')} />

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Page-level tabs */}
        <Tabs
          idPrefix="training-session"
          ariaLabel="Training session sections"
          value={String(activeTab)}
          onChange={(id) => setActiveTab(Number(id))}
          items={[
            { id: '0', label: 'Session Details' },
            {
              id: '1',
              label: session?.trainingInsights?.status === 'pending' ? 'Training Insights (running...)' : 'Training Insights',
            },
          ]}
        />

        {/* Tab: Training Insights */}
        {activeTab === 1 && (
          <TrainingInsightsTab
            insights={session?.trainingInsights}
            onNavigateToTimestamp={handleNavigateToTimestamp}
            reanalyzeButton={isAdmin ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  reanalyzing ? <CircularProgress size={16} /> : <RefreshIcon />
                }
                onClick={handleReanalyze}
                disabled={reanalyzing}
              >
                {reanalyzing ? 'Queuing…' : 'Re-run Analysis'}
              </Button>
            ) : undefined}
          />
        )}

        {/* Tab: Session Details — hidden when Training Insights tab is active */}
        {activeTab === 0 && <>

        {/* Session Overview */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Session Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon color="action" />
                  <Typography variant="body2">
                    <strong>User:</strong> {session.userName} ({session.userEmail})
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <BookIcon color="action" />
                  <Typography variant="body2">
                    <strong>Track:</strong> {session.trackName}
                  </Typography>
                  <TrackTypeBadge trackType={session.trackType} />
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <TimeIcon color="action" />
                  <Typography variant="body2">
                    <strong>Duration:</strong> {formatDuration(session.duration)}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    <strong>Status:</strong>
                  </Typography>
                  <Chip label={session.status} size="small" color={session.status === 'completed' ? 'success' : 'warning'} />
                </Box>
                <Typography variant="body2">
                  <strong>Started:</strong> {formatDate(session.startedAt)}
                </Typography>
                <Typography variant="body2">
                  <strong>Completed:</strong> {formatDate(session.completedAt || '')}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={2}>
                {/* Training quality score (behavioralScore) from post-training analysis — the
                    single source of truth (replaces the old keyword-avg
                    "AI Score"). Shows "Pending" until analysis completes. */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    <strong>Training Quality Score:</strong>
                  </Typography>
                  {behavioralScore != null ? (
                    <Chip
                      label={`${behavioralScore}%`}
                      size="small"
                      color={getScoreColor(behavioralScore)}
                    />
                  ) : (
                    <Chip label="Pending analysis" size="small" variant="outlined" />
                  )}
                </Box>
                {session.adminEvaluation && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      <strong>Admin Score:</strong>
                    </Typography>
                    <Chip
                      label={session?.adminEvaluation?.finalScore ? `${session?.adminEvaluation?.finalScore}%` : 'Not Available'}
                      size="small"
                      color={getScoreColor(session.adminEvaluation.finalScore)}
                    />
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Admin Evaluation Section - Visible to all, editable by admin only */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Admin Evaluation
            </Typography>
            {isAdmin && (
              <Box>
                {!session.adminEvaluation && !isEditingEvaluation && (
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => setIsEditingEvaluation(true)}
                  >
                    Add Evaluation
                  </Button>
                )}
                {session.adminEvaluation && !isEditingEvaluation && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditingEvaluation(true)}
                  >
                    Edit Evaluation
                  </Button>
                )}
                {isEditingEvaluation && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveEvaluation}
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIsEditingEvaluation(false);
                        // Reset form to existing evaluation
                        if (session.adminEvaluation) {
                          setFinalScore(session.adminEvaluation.finalScore);
                          setComments(session.adminEvaluation.comments);
                          setStrengths(session.adminEvaluation.strengths);
                          setImprovements(session.adminEvaluation.improvements);
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                )}
              </Box>
            )}
          </Box>
          <Divider sx={{ mb: 3 }} />

          {(!session.adminEvaluation || !session.adminEvaluation.finalScore) && !isEditingEvaluation && (
            <Alert severity="info">
              This session has not been evaluated by an administrator yet.
            </Alert>
          )}

          {session.adminEvaluation && session.adminEvaluation.finalScore !== undefined && !isEditingEvaluation && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Final Score</Typography>
                    <Chip
                      label={`${session.adminEvaluation.finalScore}%`}
                      color={getScoreColor(session.adminEvaluation.finalScore)}
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  {session.adminEvaluation.strengths && session.adminEvaluation.strengths.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="success.main" gutterBottom>Strengths</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {session.adminEvaluation.strengths.map((strength, index) => (
                          <Chip key={index} label={strength} size="small" color="success" />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {session.adminEvaluation.improvements && session.adminEvaluation.improvements.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>Areas for Improvement</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {session.adminEvaluation.improvements.map((improvement, index) => (
                          <Chip key={index} label={improvement} size="small" color="warning" />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Comments</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {session.adminEvaluation.comments || 'No comments provided.'}
                  </Typography>
                </Box>

                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary">
                    Evaluated by {session.adminEvaluation.evaluatedByEmail} on {formatDate(session.adminEvaluation.evaluatedAt)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {isEditingEvaluation && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box mb={3}>
                  <Typography gutterBottom>Final Score</Typography>
                  <Slider
                    value={finalScore}
                    onChange={(e, v) => setFinalScore(v as number)}
                    min={0}
                    max={100}
                    step={1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 50, label: '50' },
                      { value: 100, label: '100' },
                    ]}
                    valueLabelDisplay="on"
                    color={getScoreColor(finalScore)}
                  />
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Strengths
                  </Typography>
                  <Box display="flex" gap={1} mb={1}>
                    <TextField
                      size="small"
                      placeholder="Add strength..."
                      value={strengthInput}
                      onChange={(e) => setStrengthInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddStrength()}
                      fullWidth
                    />
                    <Button onClick={handleAddStrength} variant="outlined" size="small">
                      Add
                    </Button>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {strengths.map((strength, index) => (
                      <Chip
                        key={index}
                        label={strength}
                        onDelete={() => setStrengths(strengths.filter((s) => s !== strength))}
                        size="small"
                        color="success"
                      />
                    ))}
                  </Stack>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Areas for Improvement
                  </Typography>
                  <Box display="flex" gap={1} mb={1}>
                    <TextField
                      size="small"
                      placeholder="Add area for improvement..."
                      value={improvementInput}
                      onChange={(e) => setImprovementInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddImprovement()}
                      fullWidth
                    />
                    <Button onClick={handleAddImprovement} variant="outlined" size="small">
                      Add
                    </Button>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {improvements.map((improvement, index) => (
                      <Chip
                        key={index}
                        label={improvement}
                        onDelete={() => setImprovements(improvements.filter((w) => w !== improvement))}
                        size="small"
                        color="warning"
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Comments"
                  multiline
                  rows={14}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Add detailed feedback for the trainee..."
                />
              </Grid>
            </Grid>
          )}
        </Paper>

        {/* Conversation Timeline */}
        <Grid container spacing={3} mb={3}>
          <Grid size={{ xs: 12 }}>
            <Paper elevation={0} sx={{ p: 3, height: '100%', border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Conversation Timeline
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ maxHeight: '800px', overflowY: 'auto' }}>
                <div className="space-y-3">
                  {timelineEvents.map((event, index) => (
                    <TrainingTimelineEvent
                      key={index}
                      event={event}
                      timeLabel={moment(event.timestamp).format(TIME_FORMAT)}
                      inquiryActions={event.type === 'detection' && isAdmin ? {
                        isProcessed: inquiries.isProcessed(event.data?.question),
                        isSaving: inquiries.savingQuestion === event.data?.question,
                        onDismiss: () => inquiries.dismiss(event.data),
                        onAddToKnowledgeBase: () => inquiries.openSave(event.data),
                      } : undefined}
                    />
                  ))}
                </div>
              </Box>
            </Paper>
          </Grid>

        </Grid>
      </> /* end activeTab === 0 */}

        {inquiries.activeInquiry && (
          <SaveInquiryModal
            inquiry={{
              question: inquiries.activeInquiry?.question ?? '',
              answer: inquiries.activeInquiry?.answer ?? '',
              category: inquiries.activeInquiry?.category ?? '',
            }}
            onClose={inquiries.closeSave}
            onSave={inquiries.save}
            onMarkAsProcessed={inquiries.markProcessed}
            isSaving={inquiries.savingQuestion === inquiries.activeInquiry?.question}
          />
        )}
      </Box>
    </MainLayout>
  );
};

export default TrainingSessionEvaluation;
